// AppShell.tsx
// Canonical BlueRock Console shell — V3 (Lucide + mobile drawer + parent-click).
//
// No top header bar (removed — every known consumer had already opted out
// via the old `header={false}` prop, so this is now the only shape). The
// sider owns all chrome: Brand, optional WorkspaceSwitcher, nav, optional
// bottomNav, optional UserMenu (which is also where `themeToggle` renders,
// since there's no header to host it). The desktop collapse toggle is a
// floating button anchored to the sider's edge; on mobile, a floating
// hamburger trigger opens the nav drawer.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Layout, Menu, Tooltip, theme as antTheme } from 'antd';
import type { MenuProps } from 'antd';
import { ChevronRight, PanelLeft } from 'lucide-react';
import type { AppShellProps, NavItem, NavSection } from './types';
import { APP_SHELL_DEFAULTS } from './types';
import { resolveSelectedFromPath } from './nav-hierarchy/resolve';
import { resolveIcon } from './icon-registry';
import { PageContainer } from '../PageContainer';
import { Brand } from './Brand';
import { HeaderHamburger } from './HeaderHamburger';
import { MobileNavDrawer } from './MobileNavDrawer';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { UserMenu } from './UserMenu';
import { useMobileBreakpoint } from './useMobileBreakpoint';
import { CollapsedNavFlyout } from './CollapsedNavFlyout';
import { useHasBlockingOverlay } from './useHasBlockingOverlay';
import './app-shell.css';

const { Sider, Content } = Layout;

/** Stable identity for the "consumer passed no bottomNav" case. See the
 *  `bottomNavItems` comment in AppShell for why the reference must not
 *  change between renders. */
const EMPTY_NAV_ITEMS: readonly NavItem[] = [];

/** Shallow value-equality for openKeys, so an array that is a new reference
 *  but the same contents doesn't trigger a state update (and another render). */
function sameKeys(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((k, i) => k === b[i]);
}

function safeReadCollapsed(key: string): boolean {
  try { return localStorage.getItem(key) === 'true'; } catch { return false; }
}
function safeWriteCollapsed(key: string, value: boolean): void {
  try { localStorage.setItem(key, String(value)); } catch { /* noop */ }
}

/**
 * Convert a list of NavItems into AntD MenuProps['items'].
 *
 * Parent items render a custom chevron inside the label that intercepts its
 * own click (via `stopPropagation`) and toggles the submenu via `toggleOpenKey`
 * — without firing nav. Clicking the rest of the parent row falls through to
 * AntD's normal title-click handling → `onOpenChange` → nav. The chevron lets
 * users expand a section without leaving the page they're on.
 *
 * AntD's default arrow is hidden via CSS; we render our own to control its
 * hit area and hover state.
 */
function toMenuItems(
  items: readonly NavItem[],
  toggleOpenKey: (key: string) => void,
  collapsed: boolean,
  onFlyoutSelect: (key: string, item: NavItem) => void,
): MenuProps['items'] {
  return items.map((item) => {
    const Icon = resolveIcon(item.icon);
    const hasChildren = !!item.children?.length;
    const iconEl = Icon ? <Icon size={16} /> : undefined;

    // In collapsed mode, parents stay a plain leaf for AntD's own click/nav
    // handling (Menu.onClick fires as normal, resolving
    // `parent.path ?? parent.children?.[0]?.path` via the consumer's
    // onSelect) — but the icon is wrapped in a bespoke hover flyout that
    // lists the children. Not an AntD SubMenu popup: see
    // CollapsedNavFlyout.tsx for why (StrictMode + rc-motion).
    if (collapsed && hasChildren) {
      return {
        key: item.key,
        icon: <CollapsedNavFlyout item={item} icon={iconEl} onSelect={onFlyoutSelect} />,
        label: item.label,
        disabled: item.disabled,
        // Suppress AntD's own collapsed-mode auto-tooltip (it falls back to
        // the label string otherwise) — CollapsedNavFlyout's own header
        // already identifies the section, so the two would stack and show
        // duplicate "Scans" / "SCANS" text on hover.
        title: '',
      };
    }

    // Expanded mode (or leaf items): full structure. Parents get a
    // chevron-toggle inside the label for inline expansion without
    // navigation; leaves get the plain string label.
    const node: Record<string, unknown> = {
      key: item.key,
      icon: iconEl,
      // AntD renders disabled items greyed and swallows their click, so a
      // disabled leaf can't fire onSelect and can't be navigated to.
      disabled: item.disabled,
      label: hasChildren ? (
        <>
          <span
            style={{
              display: 'block',
              paddingRight: 40,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.label}
          </span>
          <span
            role="button"
            tabIndex={0}
            aria-label={`Toggle ${item.label} submenu`}
            className="br-appshell-menu-chevron"
            onClick={(e) => {
              e.stopPropagation();
              toggleOpenKey(item.key);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                toggleOpenKey(item.key);
              }
            }}
          >
            <ChevronRight size={14} />
          </span>
        </>
      ) : (
        item.label
      ),
    };
    if (hasChildren) {
      node.children = toMenuItems(item.children!, toggleOpenKey, collapsed, onFlyoutSelect) ?? [];
    }
    return node;
  }) as unknown as MenuProps['items'];
}

function toSectionedMenuItems(
  sections: readonly NavSection[],
  toggleOpenKey: (key: string) => void,
  collapsed: boolean,
  onFlyoutSelect: (key: string, item: NavItem) => void,
): MenuProps['items'] {
  // Flatten across sections. Sections WITH a heading become an AntD `group`
  // (rendered with a section label); sections WITHOUT a heading inline their
  // items directly so no empty label takes up vertical space.
  return sections.flatMap((section) => {
    const items = toMenuItems(section.items, toggleOpenKey, collapsed, onFlyoutSelect);
    if (section.heading) {
      return [{
        type: 'group' as const,
        label: section.heading,
        children: items,
      }];
    }
    return items ?? [];
  }) as unknown as MenuProps['items'];
}

function findNavItem(items: readonly NavItem[], key: string): NavItem | undefined {
  for (const item of items) {
    if (item.key === key) return item;
    if (item.children?.length) {
      const hit = findNavItem(item.children, key);
      if (hit) return hit;
    }
  }
  return undefined;
}

export function AppShell({
  brandLabel = APP_SHELL_DEFAULTS.brandLabel,
  workspace,
  user,
  themeToggle,
  children,
  pageContainer,
  nav,
  bottomNav,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  collapsePersistenceKey = APP_SHELL_DEFAULTS.collapsePersistenceKey,
  currentPath,
  selectedKeys: controlledSelected,
  onSelect,
  openKeys: controlledOpen,
  onOpenChange,
  maxContentWidth = APP_SHELL_DEFAULTS.maxContentWidth,
  siderWidth = APP_SHELL_DEFAULTS.siderWidth,
  siderCollapsedWidth = APP_SHELL_DEFAULTS.siderCollapsedWidth,
}: AppShellProps) {
  const { token } = antTheme.useToken();
  const isMobile = useMobileBreakpoint();
  const hasBlockingOverlay = useHasBlockingOverlay();

  // ── Collapse state (controlled-or-internal) ──
  const isCollapseControlled = controlledCollapsed !== undefined;
  const [internalCollapsed, setInternalCollapsed] = useState<boolean>(
    () => safeReadCollapsed(collapsePersistenceKey),
  );
  const collapsed = isCollapseControlled ? controlledCollapsed : internalCollapsed;
  useEffect(() => {
    if (!isCollapseControlled) safeWriteCollapsed(collapsePersistenceKey, internalCollapsed);
  }, [isCollapseControlled, internalCollapsed, collapsePersistenceKey]);
  // The collapse toggle's tooltip is controlled so it can be suppressed on
  // click. Its label is derived from `collapsed`, so toggling swaps the text
  // while the pointer is still over the button — AntD keeps the tooltip open
  // and it visibly flips to the opposite label ("Collapse sidebar" flashing
  // as you expand). Closing it on click and refusing to reopen until the
  // pointer leaves keeps the toggle silent through the transition.
  const [toggleTipOpen, setToggleTipOpen] = useState(false);
  const suppressToggleTip = useRef(false);

  const handleToggle = () => {
    suppressToggleTip.current = true;
    setToggleTipOpen(false);
    const next = !collapsed;
    if (!isCollapseControlled) setInternalCollapsed(next);
    onCollapsedChange?.(next);
  };

  // ── Mobile drawer state ──
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    // If transitioning back to desktop while drawer is open, close it.
    if (!isMobile && drawerOpen) setDrawerOpen(false);
  }, [isMobile, drawerOpen]);

  // ── Nav (consumer-owned, already filtered) ──
  // `bottomNav` is optional — normalize to an empty array for downstream use.
  // NOTE: this must be a stable reference. `bottomNav ?? []` allocates a fresh
  // array on every render, which changes the `derived` useMemo's deps every
  // render, which makes `derived.openKeys` a new array every render, which
  // re-fires the openKeys effect below and re-sets state — an unbounded
  // render loop. That loop starves React's concurrent work, so router
  // navigations (committed inside startTransition) never commit: the URL
  // changes but <Outlet> never re-renders.
  const bottomNavItems = bottomNav ?? EMPTY_NAV_ITEMS;

  // ── Selection ──
  const derived = useMemo(
    () => resolveSelectedFromPath(nav, bottomNavItems, currentPath),
    [nav, bottomNavItems, currentPath],
  );
  const selectedKeys = controlledSelected ?? derived.selectedKeys;

  const isOpenControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState<string[]>(derived.openKeys);
  // Compare by VALUE, not identity. `derived.openKeys` is a fresh array
  // whenever the memo recomputes, so setting it unconditionally would commit
  // a state change (a new array is never Object.is-equal) and re-render — and
  // if any input to the memo is itself unstable (e.g. a consumer passing an
  // inline `nav={[...]}` literal) that becomes an unbounded render loop that
  // starves concurrent work. Returning `prev` makes React bail out instead.
  useEffect(() => {
    if (isOpenControlled) return;
    setInternalOpen((prev) => (sameKeys(prev, derived.openKeys) ? prev : derived.openKeys));
  }, [isOpenControlled, derived.openKeys]);
  // Logical openKeys — what the user has opened. Each <Menu> below gates its
  // own display openKeys based on the effective collapsed state of where it's
  // rendered (sider vs drawer); the inner gate is the single source of truth
  // for display, so we don't repeat the collapse check here.
  const openKeys = isOpenControlled ? controlledOpen : internalOpen;

  const setOpenKeys = (keys: string[]) => {
    if (!isOpenControlled) setInternalOpen(keys);
    onOpenChange?.(keys);
  };

  // Direct toggle (used by the custom chevron in parent labels — see toMenuItems).
  // Does NOT fire nav: the chevron's stopPropagation prevents the click from
  // reaching AntD's title handler, so onOpenChange below never fires for it.
  const toggleOpenKey = (key: string) => {
    setOpenKeys(openKeys.includes(key) ? openKeys.filter((k) => k !== key) : [...openKeys, key]);
  };

  // Resolve which IA the key belongs to (sectioned first, then bottom).
  const findKey = (key: string): NavItem | undefined => {
    for (const section of nav) {
      const hit = findNavItem(section.items, key);
      if (hit) return hit;
    }
    return findNavItem(bottomNavItems, key);
  };

  // AntD's inline Menu fires onClick for LEAF items only. Parent title clicks
  // come through handleOpenChange below; the custom chevron (see toMenuItems)
  // is intercepted before AntD sees it.
  const handleClick: MenuProps['onClick'] = ({ key }) => {
    const item = findKey(key);
    if (!item) return;
    if (onSelect) onSelect(key, item);
    if (isMobile) setDrawerOpen(false);
  };

  // Kept for AntD's internal selectedKeys plumbing. Nav lives in handleClick
  // (leaves) and handleOpenChange below (parents).
  const handleSelect: MenuProps['onSelect'] = ({ key }) => {
    void key;
  };

  // Parent-click navigation: AntD calls onOpenChange when a parent's title
  // body is clicked. We detect the newly-opened key and fire onSelect for
  // it. The chevron's stopPropagation prevents chevron clicks from reaching
  // here — those are pure expand-toggles.
  //
  // When the sider is collapsed, AntD opens a hover-popup for parent items
  // (also fires onOpenChange) — that's preview, not navigation intent.
  // Skip the onSelect dispatch so hovering the collapsed nav doesn't
  // rewrite the consumer's breadcrumb / path.
  const handleOpenChange = (keys: string[]) => {
    const newlyOpenedKey = keys.find((k) => !openKeys.includes(k));
    setOpenKeys(keys);
    if (newlyOpenedKey && onSelect && !collapsed) {
      const item = findKey(newlyOpenedKey);
      if (item) onSelect(newlyOpenedKey, item);
    }
  };

  /**
   * Render the sider's inner content. Called twice: once for the actual
   * <Sider> (using real `collapsed`), once for the <MobileNavDrawer> (using
   * `false` because the drawer is always full-width). The locked slots —
   * workspace + user — render the canonical helpers with the effective
   * collapsed value injected; consumers pass their props, not React content. */
  // Brand-click navigates Home. We look up 'home' in the filtered nav so
  // brand-click respects the consumer's role/flag gating — if the user
  // can't see Home in the menu, the brand isn't clickable either.
  const homeItem = findKey('home');
  const handleBrandClick = homeItem && onSelect
    ? () => {
        onSelect('home', homeItem);
        if (isMobile) setDrawerOpen(false);
      }
    : undefined;

  const renderSiderContent = (effectiveCollapsed: boolean) => {
    const ws = workspace ? <WorkspaceSwitcher {...workspace} collapsed={effectiveCollapsed} /> : null;
    const sf = user ? (
      <UserMenu
        {...user}
        collapsed={effectiveCollapsed}
        themeToggle={themeToggle}
      />
    ) : null;
    // Menu items computed per-render with the effective collapsed value so
    // collapsed-only treatments (parents-as-leaves, flyout) apply to the
    // desktop sider but NOT to the mobile drawer (which is always
    // full-width — CollapsedNavFlyout only mounts when collapsed is true).
    const handleFlyoutSelect = (key: string, item: NavItem) => onSelect?.(key, item);
    const sectionedMenuItems = toSectionedMenuItems(nav, toggleOpenKey, effectiveCollapsed, handleFlyoutSelect);
    const bottomMenuItems = toMenuItems(bottomNavItems, toggleOpenKey, effectiveCollapsed, handleFlyoutSelect);

    // In collapsed mode, parents render as leaves; children are reachable
    // via the CollapsedNavFlyout hover popup rather than inline. The
    // resolved selected key still points at a child
    // (e.g., 'observability-charts'), which isn't rendered. Augment the
    // selected list with each selected key's top-level ancestor so the
    // parent's leaf-form picks up the active state — and the active
    // appearance persists seamlessly through the collapse/expand
    // transition.
    const effectiveSelectedKeys = effectiveCollapsed
      ? selectedKeys.flatMap((k) => {
          const ancestorKey = (() => {
            for (const section of nav) {
              for (const top of section.items) {
                if (top.key === k) return top.key;
                if (top.children?.some((c) => c.key === k)) return top.key;
              }
            }
            return undefined;
          })();
          return ancestorKey && ancestorKey !== k ? [k, ancestorKey] : [k];
        })
      : selectedKeys;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Brand collapsed={effectiveCollapsed} onClick={handleBrandClick} label={brandLabel} />
        {ws}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <Menu
            mode="inline"
            inlineCollapsed={effectiveCollapsed}
            selectedKeys={effectiveSelectedKeys}
            openKeys={effectiveCollapsed ? [] : openKeys}
            onOpenChange={handleOpenChange}
            onClick={handleClick}
            onSelect={handleSelect}
            items={sectionedMenuItems}
            className="br-appshell-menu"
            style={{ background: 'transparent', border: 'none', fontSize: 14 }}
          />
        </div>
        {bottomMenuItems && bottomMenuItems.length > 0 && (
          <Menu
            mode="inline"
            inlineCollapsed={effectiveCollapsed}
            selectedKeys={effectiveSelectedKeys}
            onClick={handleClick}
            onSelect={handleSelect}
            items={bottomMenuItems}
            className="br-appshell-menu br-appshell-menu--bottom"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 14,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
            }}
          />
        )}
        {sf}
      </div>
    );
  };

  const sw = isMobile ? 0 : (collapsed ? siderCollapsedWidth : siderWidth);

  // FlyoutDrawer's backdrop reads `var(--sider-width, 0px)` so it stops at
  // the sider's edge instead of covering it — but nothing was ever setting
  // that variable, so it silently fell back to 0px and the backdrop covered
  // the ENTIRE viewport (including the sider) whenever a flyout's backdrop
  // was enabled, swallowing hover/click on every nav item, not just the
  // floating collapse toggle. Set on `documentElement` (not a ref inside
  // this component's own tree) because Drawer/backdrop portal to
  // `document.body` — a CSS custom property only reaches a portal through
  // a shared DOM ancestor, and `<html>` is the nearest one both sides share.
  useEffect(() => {
    document.documentElement.style.setProperty('--sider-width', `${sw}px`);
    return () => {
      document.documentElement.style.removeProperty('--sider-width');
    };
  }, [sw]);

  return (
    <Layout style={{ height: '100vh' }}>
      <Sider
        className="br-appshell-sider"
        width={siderWidth}
        collapsedWidth={siderCollapsedWidth}
        collapsed={collapsed}
        collapsible
        trigger={null}
        style={{
          background: token.colorBgContainer,
          height: '100vh',
          position: 'fixed',
          left: 0, top: 0, bottom: 0,
          zIndex: 200,
          overflow: 'hidden',
          // colorBorderSecondary, not colorBorder — the latter is 2x the alpha
          // (0.08 vs 0.04 in dark) and reads as a heavy rule against the
          // full height of the viewport. This also matches the divider weight
          // used elsewhere in the shell chrome (FlyoutDrawer's strip, the
          // UserMenu popover) so the app's vertical edges are consistent.
          borderRight: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        {renderSiderContent(collapsed)}
      </Sider>

      {!isMobile && (
        // Floating edge toggle: straddles the sider's right border, so it
        // never reflows/squeezes with the brand content at any collapse
        // width — it isn't part of that layout at all. `left` tracks the
        // live sider width so it slides with the sider's own collapse
        // transition. `top: 27` matches the brand icon's actual rendered
        // center (Brand.tsx's 48px row centers its 22px icon within a
        // 26px content box — 14px top padding + 8px bottom — landing the
        // icon center at y=27, not the row's own midpoint of y=24). The
        // toggle was never part of a header's own flow, so it's positioned
        // the same way now that there is no header at all.
        //
        // Because it straddles that border, half the button's hit area
        // sits inside the content area — the same region a Flyout's
        // backdrop covers (`left: var(--sider-width)`, see FlyoutDrawer).
        // Flyout z-index is dynamic (1000 + stackIndex*100, see
        // flyout-stack.ts); 9999 comfortably clears any realistic stack
        // depth so the toggle stays hoverable/clickable above an open
        // flyout instead of being intercepted by its backdrop.
        // `data-flyout-keep-open` additionally exempts it from
        // FlyoutDrawer's outside-click-closes listener, so toggling the
        // sider doesn't dismiss whatever flyout is open.
        //
        // That same 9999 also cleared AntD's Modal/Drawer mask (z-index
        // 1000), leaving the toggle visibly un-shaded above an open modal —
        // Flyouts and Modals both want "on top", but only one should win at
        // a time, and a modal always should. `useHasBlockingOverlay` detects
        // any open Modal/Drawer mask via a subtree MutationObserver (see its
        // own file for why it keys off the mask rather than the
        // Modal/Drawer root, and why a DOM check rather than a tracked-state
        // hook — corenet can't depend on an app-local modal registry), and
        // the toggle drops to 260 while one is open so the mask shades it
        // like everything else on the page. Not `token.zIndexBase` (0) —
        // that's below the Sider's own z-index (200 above), so the toggle
        // rendered behind the sidebar itself instead of merely under the
        // modal mask. 260 stays above the Sider (200) and the mobile header
        // trigger (250 below) while staying under the mask (1000).
        <Tooltip
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          placement="right"
          open={toggleTipOpen}
          onOpenChange={(next) => {
            // Ignore reopen requests until the pointer leaves (see
            // suppressToggleTip above) — otherwise AntD immediately re-opens
            // the tooltip after the click, showing the swapped label.
            if (next && suppressToggleTip.current) return;
            setToggleTipOpen(next);
          }}
        >
          <Button
            shape="circle"
            type="default"
            size="small"
            // Static by design — a left-chevron here reads as "back". State
            // lives in the aria-label/tooltip below, not the glyph.
            icon={<PanelLeft size={14} />}
            onClick={handleToggle}
            onMouseLeave={() => {
              suppressToggleTip.current = false;
            }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-flyout-keep-open
            style={{
              position: 'fixed',
              left: sw,
              top: 27,
              transform: 'translate(-50%, -50%)',
              zIndex: hasBlockingOverlay ? 260 : 9999,
              boxShadow: token.boxShadowTertiary,
              transition: 'left .2s',
            }}
          />
        </Tooltip>
      )}

      {isMobile && (
        // There's no header to host a HeaderHamburger, so a minimal floating
        // trigger keeps the nav drawer reachable on mobile.
        <div
          style={{
            position: 'fixed',
            top: 8,
            left: 8,
            zIndex: 250,
            background: token.colorBgContainer,
            borderRadius: 8,
            border: `1px solid ${token.colorBorder}`,
            boxShadow: token.boxShadowSecondary,
          }}
        >
          <HeaderHamburger onOpen={() => setDrawerOpen(true)} />
        </div>
      )}

      <MobileNavDrawer open={isMobile && drawerOpen} onClose={() => setDrawerOpen(false)}>
        {renderSiderContent(false)}
      </MobileNavDrawer>

      <Layout
        className="br-appshell-main-layout"
        style={{
          marginLeft: sw,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          transition: 'margin-left .2s',
        }}
      >
        <Content
          tabIndex={0}
          aria-label="Page content"
          style={{
            background: token.colorBgLayout,
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            transition: 'background .2s',
            maxWidth: maxContentWidth,
            width: '100%',
            alignSelf: 'flex-start',
          }}
        >
          {pageContainer === false
            ? children
            : <PageContainer {...(pageContainer ?? {})}>{children}</PageContainer>}
        </Content>
      </Layout>
    </Layout>
  );
}

// No compound subcomponents — every chrome/sider slot is typed-props.
// Consumers configure via AppShellProps; they never directly render any of
// the internal helpers (Brand, WorkspaceSwitcher, UserMenu, HeaderHamburger,
// MobileNavDrawer). There is no header row — see the module doc comment.
